// Section hero : la première chose que voit l'utilisateur après le chargement.
// Gère les interactions avec la télévision CRT : clic pour scroller vers le menu,
// survol des items du menu (prévisualisations 3D + navigation), et animation
// du titre au fil du scroll. Récupère le menuPreview préparé pendant le chargement.

import type { SectionContext, SectionLifecycle } from '../types';
import { applyCrtModelPreview } from '../../crt/crtModelPreview';
import { clamp01 } from '../../utils/math';
import { BASELINE_VIEWPORT_HEIGHT } from '../../crt/crtConfig';
import { hasWebGLSupport } from '../../core/gpuCapabilities';
import initHeroFallback from './heroFallback';
import {
  getSectionSelector,
  SECTION_IDS,
  crtMenuSectionIds,
} from '../definitions';
import type { MenuPreview3D } from '../../components/3d/menuPreview3D';
import { createAccessibilityMenu } from './heroAccessibility';
import { createHeroRaycaster } from './heroRaycaster';

// ── Extras typés ──────────────────────────────────────────────────────────────

/** Extras typés transmis par le loading screen à la section hero. */
export interface HeroExtras extends Record<string, unknown> {
  menuPreview?: MenuPreview3D;
}

// ── Section initializer ────────────────────────────────────────────────────────

export const initHeroSection = async (context: SectionContext<HeroExtras>): Promise<SectionLifecycle> => {
  // Fallback for browsers without WebGL support
  if (!hasWebGLSupport()) {
    console.warn('WebGL not supported, using HTML fallback for hero section');
    const fallbackResult = initHeroFallback(context);
    return fallbackResult instanceof Promise
      ? await fallbackResult
      : fallbackResult;
  }

  const { camera, renderer, crtManager } = context;

  // ── Récupération du menuPreview pré-créé par le loading screen ────────────
  if (!context.extras?.menuPreview) {
    throw new Error('[hero] menuPreview est requis dans context.extras (initialisé par loadingScreen)');
  }

  const { menuPreview } = context.extras;

  // ── Détection hover menu via raycaster ─────────────────────
  const heroElement = document.querySelector(getSectionSelector(SECTION_IDS.HERO));
  const menuElement = document.querySelector(getSectionSelector(SECTION_IDS.HUB_CENTRAL));
  const heroRaycaster = createHeroRaycaster(
    camera,
    renderer,
    crtManager.mesh,
    menuElement,
  );
  let hoverMenuIndex = -1;
  let currentMenuOpacity = 0;

  // ── Élément invisible focusable pour la section hero ────
  const heroFocusElement = document.createElement('button');
  heroFocusElement.setAttribute('aria-label', 'Section Hero - Luv Resval');
  heroFocusElement.className = 'hero-focus-anchor';

  if (heroElement) heroElement.appendChild(heroFocusElement);

  // ── Boutons accessibles pour le menu ────────────────────────
  const accessibilityMenu = createAccessibilityMenu(
    (index) => {
      const targetSectionId = crtMenuSectionIds[index];
      if (targetSectionId)
        context.scrollManager.scrollToSection(targetSectionId);
    },
    (index) => {
      hoverMenuIndex = index;
    },
  );

  // ── Interactions post-loading ──────────────────────────────────────────────

  let cursorIsPointer = false;

  const setCursor = (pointer: boolean): void => {
    if (pointer === cursorIsPointer) return;
    document.body.style.cursor = pointer ? 'pointer' : '';
    cursorIsPointer = pointer;
  };

  const onMouseMove = (event: MouseEvent): void => {
    // Sections après le menu (big brother, mpc…) : ne pas interférer avec le curseur
    if (menuElement instanceof HTMLElement && menuElement.getBoundingClientRect().bottom <= 0) {
      setCursor(false);
      hoverMenuIndex = -1;
      return;
    }

    if (!heroRaycaster.isAtMenuSection()) {
      hoverMenuIndex = -1;
      // Section hero : pointer si la souris est sur la TV CRT
      setCursor(heroRaycaster.isClickOnCrt(event.clientX, event.clientY));
      return;
    }
    try {
      const prevHoverMenuIndex = hoverMenuIndex;
      hoverMenuIndex = heroRaycaster.getHoverMenuIndexFromPointer(
        event.clientX,
        event.clientY,
        currentMenuOpacity,
      );
      if (hoverMenuIndex !== prevHoverMenuIndex && hoverMenuIndex >= 0) {
        context.audioManager.playUiFx();
      }
      // Section menu : pointer si on survole un item
      setCursor(hoverMenuIndex >= 0);
    } catch (error) {
      console.warn('Raycasting hover detection failed:', error);
      hoverMenuIndex = -1;
      setCursor(false);
    }
  };

  const onClick = (event: MouseEvent): void => {
    if (!heroRaycaster.isClickOnCrt(event.clientX, event.clientY)) return;

    if (heroRaycaster.isAtMenuSection()) {
      // Dans la section menu : naviguer vers l'item cliqué
      hoverMenuIndex = heroRaycaster.getHoverMenuIndexFromPointer(
        event.clientX,
        event.clientY,
        currentMenuOpacity,
      );
      if (hoverMenuIndex >= 0 && hoverMenuIndex < crtMenuSectionIds.length) {
        const targetSectionId = crtMenuSectionIds[hoverMenuIndex];
        if (targetSectionId)
          context.scrollManager.scrollToSection(targetSectionId);
      }
      return;
    }

    // On est passé après le menu (sections 1/2/3, Outro) → ignorer le clic
    if (menuElement instanceof HTMLElement && menuElement.getBoundingClientRect().bottom <= 0) {
      return;
    }

    // Dans la section hero : tout clic sur la TV scroll vers le menu.
    context.scrollManager.scrollToSection(SECTION_IDS.HUB_CENTRAL, BASELINE_VIEWPORT_HEIGHT);
  };

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('click', onClick);

  return {
    update: (_deltaSeconds: number, elapsedSeconds: number) => {
      // crtManager.update() est appelé centralement dans main.ts — ne pas le rappeler ici.
      const scrollY = context.scrollManager.getScrollY();

      const heroProgress = clamp01(scrollY / BASELINE_VIEWPORT_HEIGHT);
      const menuOpacity = clamp01(
        (scrollY / BASELINE_VIEWPORT_HEIGHT - 0.5) / 0.5,
      );
      currentMenuOpacity = menuOpacity;

      // loadingProgress = 2 : transition loading complète, vue héro active
      crtManager.setUiProgress(heroProgress, menuOpacity, hoverMenuIndex, 2, false, elapsedSeconds * 1000);

      menuPreview.setHoveredIndex(heroRaycaster.isAtMenuSection() ? hoverMenuIndex : -1);
      menuPreview.update(_deltaSeconds);
      menuPreview.renderPreview();
      applyCrtModelPreview(crtManager, {
        texture: menuPreview.getTexture(),
        opacity: menuPreview.getOpacity(),
        texelSize: menuPreview.getTexelSize(),
      });

      accessibilityMenu.updateVisibility(menuOpacity, heroRaycaster.isAtMenuSection());
    },
    dispose: () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click', onClick);
      setCursor(false);
      accessibilityMenu.dispose();

      if (heroFocusElement.parentNode) {
        heroFocusElement.parentNode.removeChild(heroFocusElement);
      }

      menuPreview.dispose();
      // Note : le CRT mesh n'est PAS disposé ici — il est global et géré par main.ts
    },
  };
};
